import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

export const fetchConversations = createAsyncThunk(
  'inbox/fetchConversations',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams(filters).toString();
      const { data } = await api.get(`/inbox/conversations?${params}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch conversations');
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'inbox/fetchMessages',
  async ({ conversationId, page = 1 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/inbox/conversations/${conversationId}/messages?page=${page}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch messages');
    }
  }
);

export const sendReply = createAsyncThunk(
  'inbox/sendReply',
  async ({ conversationId, text, media }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/inbox/conversations/${conversationId}/reply`, { text, ...(media || {}) });
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to send reply');
    }
  }
);

export const addNote = createAsyncThunk(
  'inbox/addNote',
  async ({ conversationId, text }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/inbox/conversations/${conversationId}/note`, { text });
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to add note');
    }
  }
);

export const assignConversation = createAsyncThunk(
  'inbox/assignConversation',
  async ({ conversationId, agentId }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/inbox/conversations/${conversationId}/assign`, { agentId });
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to assign conversation');
    }
  }
);

export const updateConversationStatus = createAsyncThunk(
  'inbox/updateConversationStatus',
  async ({ conversationId, status }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/inbox/conversations/${conversationId}/status`, { status });
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to update status');
    }
  }
);

// Hand a conversation to staff ('human') or back to the AI agent ('ai').
export const setConversationAiState = createAsyncThunk(
  'inbox/setConversationAiState',
  async ({ conversationId, aiState }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/inbox/conversations/${conversationId}/ai-state`, { aiState });
      return { conversationId, aiState: data.data.aiState };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to update AI state');
    }
  }
);

export const markConversationAsLead = createAsyncThunk(
  'inbox/markConversationAsLead',
  async (conversationId, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/inbox/conversations/${conversationId}/mark-as-lead`);
      return data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to mark as lead');
    }
  }
);

const inboxSlice = createSlice({
  name: 'inbox',
  initialState: {
    conversations: [],
    pagination: null,
    activeConversationId: null,
    messages: [],
    messagesPagination: null,
    loading: false,
    messagesLoading: false,
    messagesLoadingMore: false,
    error: null,
    filters: { channel: 'all', status: 'open', search: '' },
  },
  reducers: {
    setActiveConversation(state, action) {
      state.activeConversationId = action.payload;
      state.messages = [];
      state.messagesPagination = null;
    },
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    appendMessage(state, action) {
      const msg = action.payload;
      if (state.activeConversationId === msg.conversationId) {
        const exists = state.messages.find((m) => m._id === msg._id);
        if (!exists) state.messages.push(msg);
      }
      // Update conversation preview
      const conv = state.conversations.find((c) => c._id === msg.conversationId);
      if (conv) {
        conv.lastMessagePreview = msg.content?.text || `[${msg.type}]`;
        conv.lastMessageAt = msg.createdAt;
        if (msg.direction === 'inbound') conv.unreadCount = (conv.unreadCount || 0) + 1;
      }
    },
    markConversationRead(state, action) {
      const conv = state.conversations.find((c) => c._id === action.payload);
      if (conv) conv.unreadCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        const newList = action.payload.data;
        // Keep the currently open conversation around even if a status change
        // (e.g. Resolve) makes it fall out of the active filter — otherwise a
        // refetch triggered by the 'inbox:conversation_updated' socket event
        // silently drops it from `conversations`, activeConv resolves to
        // undefined, and the open chat window appears to forcibly close.
        if (state.activeConversationId && !newList.some((c) => c._id === state.activeConversationId)) {
          const stillActive = state.conversations.find((c) => c._id === state.activeConversationId);
          if (stillActive) newList.push(stillActive);
        }
        state.conversations = newList;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchConversations.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(fetchMessages.pending, (state, action) => {
        // page 1 is the initial full-thread load (shows the big spinner);
        // page 2+ is "load older history" — keep the existing thread visible
        // and only show the small inline spinner, so scroll position doesn't jump.
        if ((action.meta.arg.page || 1) > 1) state.messagesLoadingMore = true;
        else state.messagesLoading = true;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        state.messagesLoadingMore = false;
        state.messagesPagination = action.payload.pagination;
        if ((action.meta.arg.page || 1) > 1) {
          // Older page: prepend, skipping any messages already in state (a
          // message sent while the older page was loading could otherwise dupe).
          const existingIds = new Set(state.messages.map((m) => m._id));
          const older = action.payload.data.filter((m) => !existingIds.has(m._id));
          state.messages = [...older, ...state.messages];
        } else {
          state.messages = action.payload.data;
        }
      })
      .addCase(fetchMessages.rejected, (state) => {
        state.messagesLoading = false;
        state.messagesLoadingMore = false;
      })

      .addCase(sendReply.fulfilled, (state, action) => {
        const msg = action.payload;
        const exists = state.messages.find((m) => m._id === msg._id);
        if (!exists) state.messages.push(msg);
      })
      .addCase(addNote.fulfilled, (state, action) => {
        state.messages.push(action.payload);
      })
      .addCase(updateConversationStatus.fulfilled, (state, action) => {
        const idx = state.conversations.findIndex((c) => c._id === action.payload._id);
        if (idx !== -1) state.conversations[idx] = action.payload;
      })
      .addCase(setConversationAiState.fulfilled, (state, action) => {
        const conv = state.conversations.find((c) => c._id === action.payload.conversationId);
        if (conv) conv.aiState = action.payload.aiState;
      })
      .addCase(markConversationAsLead.fulfilled, (state, action) => {
        const idx = state.conversations.findIndex((c) => c._id === action.payload.conversation._id);
        if (idx !== -1) state.conversations[idx] = action.payload.conversation;
      });
  },
});

export const { setActiveConversation, setFilters, appendMessage, markConversationRead } = inboxSlice.actions;
export default inboxSlice.reducer;
